import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { ChevronDownExactIcon, ChevronUpExactIcon } from '../icons'

interface ClampableTextProps {
  children: string
  clamp?: number
}

export const ClampableText = (props: ClampableTextProps) => {
  const descriptionRef = useRef<HTMLDivElement>(null)
  const [isOpened, setIsOpened] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    if (!descriptionRef.current) {
      return
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsTruncated(entry.target.scrollHeight > entry.contentRect.height)
      }
    })

    observer.observe(descriptionRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <Root
      onClick={() => {
        if (!isOpened && !isTruncated) {
          return
        }
        setIsOpened((x) => !x)
      }}
    >
      <Text
        $clamp={props.clamp ?? 3}
        className={clsx(
          isOpened ? 'opened' : 'closed',
          isTruncated ? 'truncated' : 'not-truncated',
        )}
        ref={descriptionRef}
      >
        {props.children}
      </Text>
      <Toggle
        className={clsx(
          isOpened ? 'opened' : 'closed',
          isTruncated ? 'truncated' : 'not-truncated',
        )}
        type="button"
      >
        {isOpened ? (
          <ChevronUpExactIcon color="white" width={8} />
        ) : (
          <ChevronDownExactIcon color="white" width={8} />
        )}
      </Toggle>
    </Root>
  )
}

const Root = styled.div`
  position: relative;
`

const Text = styled.div<{ $clamp: number }>`
  word-break: break-word;
  overflow: hidden;
  line-clamp: ${(_) => _.$clamp};

  &.opened {
    line-clamp: unset;
  }
`

const Toggle = styled.button`
  all: unset;
  line-height: inherit;
  font-size: inherit;
  color: inherit;
  cursor: pointer;
  background: var(--appearance-1);
  text-align: right;
  position: absolute;
  display: inline-flex;
  bottom: 0;
  right: 0;
  height: 1lh;
  padding: 0 0 0 2px;
  &.closed.not-truncated {
    display: none;
  }
`
